/**
 * Vignettes gravées — refonte « page de presse 1888 » (2026-08-02).
 *
 * Les quotidiens illustrés de la fin du XIXe ouvraient sur une gravure sur
 * bois : le pont qu'on inaugure, la halle qu'on bâtit, l'usine qui fume.
 * C'est l'imaginaire recherché ici — non pas décorer, mais dire que quelque
 * chose se construit et avance, mois après mois.
 *
 * Tracé au trait, à l'encre, sans aucun aplat : `currentColor` uniquement,
 * donc la vignette prend la couleur du contexte typographique où on la pose.
 * Zéro asset, zéro fetch — même parti pris que la carte du monde
 * (`WorldMap.tsx`), dont on reprend le filtre de tremblé.
 */

interface OrnamentProps {
  /** Côté du carré, en px. 40-56 dans les têtes de rubrique. */
  size?: number;
  className?: string;
}

/** Tremblé « gravé à la main » : bruit fractal léger qui déplace les traits. */
function RoughFilter({ id }: { id: string }) {
  return (
    <filter id={id} x="-6%" y="-6%" width="112%" height="112%">
      <feTurbulence
        type="fractalNoise"
        baseFrequency="0.05"
        numOctaves={2}
        seed={11}
        result="noise"
      />
      <feDisplacementMap
        in="SourceGraphic"
        in2="noise"
        scale={1.1}
        xChannelSelector="R"
        yChannelSelector="G"
      />
    </filter>
  );
}

const svgProps = (size: number, className?: string) => ({
  viewBox: "0 0 64 64",
  width: size,
  height: size,
  className: ["ornament", className].filter(Boolean).join(" "),
  role: "presentation" as const,
  focusable: "false" as const,
  "aria-hidden": true,
});

/**
 * Échafaudage — ce qu'on est en train de monter.
 * Montants, planchers, contreventements, fanion de chantier au sommet.
 */
export function OrnamentScaffold({ size = 48, className }: OrnamentProps) {
  return (
    <svg {...svgProps(size, className)}>
      <defs>
        <RoughFilter id="orn-rough-scaffold" />
      </defs>
      <g
        filter="url(#orn-rough-scaffold)"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      >
        {/* Montants */}
        <path d="M15 56V13M49 56V13" />
        {/* Planchers */}
        <path d="M15 21h34M15 33h34M15 45h34" />
        {/* Contreventements — une diagonale par travée, alternées */}
        <path d="M15 33 49 21M49 45 15 33" strokeWidth="1" />
        {/* Sol */}
        <path d="M7 56h50" strokeWidth="1.8" />
        {/* Fanion de chantier */}
        <path d="M32 13V6M32 6l9 3-9 3" strokeWidth="1.2" />
      </g>
    </svg>
  );
}

/**
 * Pont en construction — la travée qu'on lance par-dessus le vide.
 * Suspension entre deux pylônes, tablier, eau hachurée.
 */
export function OrnamentBridge({ size = 48, className }: OrnamentProps) {
  return (
    <svg {...svgProps(size, className)}>
      <defs>
        <RoughFilter id="orn-rough-bridge" />
      </defs>
      <g
        filter="url(#orn-rough-bridge)"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      >
        {/* Tablier */}
        <path d="M3 42h58" strokeWidth="1.8" />
        {/* Pylônes */}
        <path d="M20 42V13M44 42V13" />
        <path d="M17 13h6M41 13h6" strokeWidth="1.2" />
        {/* Câble porteur : deux retombées + une chaînette centrale */}
        <path d="M3 30 20 13M44 13l17 17" strokeWidth="1.1" />
        <path d="M20 13q12 20 24 0" strokeWidth="1.1" />
        {/* Suspentes */}
        <path
          d="M26 20v22M32 23v19M38 20v22M12 22v20M52 22v20"
          strokeWidth="0.9"
        />
        {/* Eau */}
        <path
          d="M6 50h14M26 50h12M44 50h14M14 55h16M38 55h12"
          strokeWidth="1"
        />
      </g>
    </svg>
  );
}

/**
 * Cheminée d'usine — la production qui tourne.
 * Fût cerclé, bâti d'atelier, fumée qui monte.
 */
export function OrnamentChimney({ size = 48, className }: OrnamentProps) {
  return (
    <svg {...svgProps(size, className)}>
      <defs>
        <RoughFilter id="orn-rough-chimney" />
      </defs>
      <g
        filter="url(#orn-rough-chimney)"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      >
        {/* Fût, légèrement tronconique */}
        <path d="M26 56 29 18M44 56 41 18" />
        <path d="M27 18h16" strokeWidth="1.8" />
        {/* Cerclages */}
        <path d="M29.5 26h11M28.6 36h12.8M27.7 46h14.6" strokeWidth="0.9" />
        {/* Bâti d'atelier */}
        <path d="M6 56V44l10-6 10 6" strokeWidth="1.3" />
        <path d="M4 56h56" strokeWidth="1.8" />
        {/* Fumée */}
        <path d="M33 14q-5-4 0-7t0-5" strokeWidth="1.1" strokeLinecap="round" />
        <path d="M39 13q4-3 1-6t2-4" strokeWidth="0.9" />
      </g>
    </svg>
  );
}
